import {
  CreateProductModalState,
  NewProductModal,
  ProductDetailsModal,
  ListingTypeVariantConfig,
} from "../../../interfaces/ProductObject";
import { UserPreferences } from "../../../interfaces/UserObject";
import { MediaRecord } from "../../../interfaces/MediaObject";
import genericUtility from "../../utility/GenericUtility";
import {
  convertProductDetailsToNewProduct,
  SERVER_OWNED_DETAIL_KEYS,
} from "../backendToUi/productConvertor";
import { sanitizeSectionNameForSave } from "../../helpers/BusinessSectionsHelper";
import CreateProductConfigHelper from "../../helpers/CreateProductConfigHelper";
import { isInfoListingType } from "../../helpers/InfoListingHelper";
import { buildInfoRequestParts } from "../../helpers/InfoDraftHelper";
function buildCreateProductPayload(
  newProduct: CreateProductModalState,
  currentUserLocation: any,
  // Optional on purpose: the internal bulk-clone call below (which has no
  // per-listing-type config in scope) omits it and keeps the pre-existing,
  // config-unaware behavior. Every real create/update call site now passes
  // it (even as null for "not supported") so that flow gets the same
  // config-aware answer StrategicDetails and validateStrategy use — see
  // CreateProductConfigHelper.areVariantsActive.
  variantsConfig?: ListingTypeVariantConfig | null
): any {
  const { essentials, description, strategy, keyAttributes } = newProduct;

  // ---- Merge mandatory + optional into a single details map ----
  // add age as well in this if ListingType is Used Product

  const mergedDetails: Record<string, string | number | null> = {
    ...(keyAttributes?.mandatoryAttributes || {}),
  };

  if (keyAttributes?.optionalAttributes) {
    Object.values(keyAttributes.optionalAttributes).forEach((attr: any) => {
      if (!attr.key || attr.key.trim() === "") return;
      // Unfilled optional rows (config attributes now arrive as empty optional rows)
      // must not reach the backend as empty keys.
      if ((attr.value ?? "").toString().trim() === "") return;
      const lowerKey = attr.key.trim().toLowerCase();
      // A cleared section must drop the key entirely — never send an empty section.
      if (lowerKey === "section" && (attr.value ?? "").toString().trim() === "") {
        return;
      }
      // Safety net: the backend can't handle whitespace in section/sub-section
      // values. The dedicated pickers already save the dash form, but this
      // catches anything that reaches here un-sanitized (e.g. the personal-
      // listing free-text sub-section field) so a space can never slip
      // through to the create-product payload.
      if (lowerKey === "section" || lowerKey === "subsection") {
        mergedDetails[attr.key] = sanitizeSectionNameForSave(String(attr.value ?? ""));
        return;
      }
      mergedDetails[attr.key] = attr.value;
    });
  }

  // `details` is a FULL REPLACE on update, same as every other key in this
  // map — so essentials.sectionId (populated on load by
  // convertProductDetailsToNewProduct, or set by a screen using the tree
  // picker) must be forwarded on EVERY save, not only when the section
  // screen itself was touched, or an unrelated edit would silently clear a
  // previously-assigned section. Only omitted when `undefined` — meaning the
  // product/screen never had a section to begin with, so there's nothing to
  // preserve; `null` (an explicit clear) is still sent. See
  // convertProductDetailsToNewProduct's matching comment for why that
  // distinction is kept instead of collapsing straight to `null`.
  if (essentials.sectionId !== undefined) {
    mergedDetails.sectionId = essentials.sectionId;
  }

  if (essentials.age) {
    mergedDetails["age"] = essentials.age;
  }

  if (essentials.sharedAttributes) {
    Object.entries(essentials.sharedAttributes).forEach(([key, val]) => {
      mergedDetails[key] = Array.isArray(val) ? val.join(",") : val;
    });
  }

  if (strategy.rentalPricing) {
    Object.entries(strategy.rentalPricing).forEach(([key, val]) => {
      if (val) mergedDetails[`rental_${key}`] = val;
    });
  }

  if (strategy.depositAmount) {
    mergedDetails["deposit_amount"] = strategy.depositAmount;
  }

  // ---- Variants ----
  // Kept as their own block, separate from mergedDetails (the flat attribute
  // map) — see ProductVariant. Omitted entirely for a product that never had
  // variants, so the wire shape for every non-variant product is unchanged.
  // An update clearing a previously-variant product's rows still has to say
  // so explicitly (hasVariants:false + variants:[]) rather than omit the
  // key — omitting `variants` on update means "don't touch" server-side.
  const variantDrafts = strategy.variantDrafts ?? [];
  const variantsActive =
    variantsConfig !== undefined
      ? CreateProductConfigHelper.areVariantsActive(variantsConfig, strategy)
      : !!strategy.variantsEnabled && variantDrafts.length > 0;
  const hadVariantsOnLoad = !!strategy.variantsWereEnabledOnLoad;
  const variantsPayload: Record<string, unknown> = variantsActive
    ? {
        hasVariants: true,
        variantAxisLabel: strategy.variantAxisLabel || undefined,
        variants: variantDrafts.map((v, idx) => ({
          ...(v.variantId ? { variantId: v.variantId } : {}),
          label: v.label,
          price: Number(v.price),
          // v.originalPrice is `null` (not just absent) for a variant that never
          // had one -- Number(null) === 0 in JS, so naively coercing here used to
          // manufacture an `originalPrice: 0` on every save of a variant that was
          // never meant to have one, which then failed the backend's
          // `originalPrice >= price` check for any positive price. Preserve null
          // as null instead of running it through Number().
          ...(v.originalPrice !== undefined && v.originalPrice !== ""
            ? { originalPrice: v.originalPrice === null ? null : Number(v.originalPrice) }
            : {}),
          sortOrder: idx,
          isDefault: v.isDefault ?? idx === 0,
          isAvailable: v.isAvailable ?? true,
          ...(v.sku ? { sku: v.sku } : {}),
          ...(v.mediaPaths && v.mediaPaths.length > 0
            ? { mediaPaths: v.mediaPaths }
            : {}),
        })),
      }
    : hadVariantsOnLoad
    ? { hasVariants: false, variants: [] }
    : {};

  // Only already-persisted media can be referenced by the backend — a "new"
  // item still uploading (or one whose upload failed) has no MediaRecord yet,
  // so it's dropped here rather than sent as a hole in the array. Bytes are
  // never re-sent; this only carries the objectPath/publicUrl references.
  const mediaResources: MediaRecord[] = (essentials.media || []).reduce<MediaRecord[]>(
    (records, item) => {
      if (item.status === "existing") records.push(item.record);
      else if (item.upload.state === "done") records.push(item.upload.record);
      return records;
    },
    []
  );

  // Info listings: no price, no variants, no attributes — just the page body,
  // its sponsor and the template tag. Built as a separate payload so the
  // generic path below stays byte-for-byte what it was.
  if (isInfoListingType(essentials.listingType)) {
    return {
      product: {
        title: description.title,
        description: description.description,
        category: description.category || "others",
        sellerId: genericUtility.getCurrentUserId(),
        details: { INFO_TEMPLATE: newProduct.infoDraft?.template ?? "blank" },
        listingType: essentials.listingType,
        sellingPrice: null,
        isNegotiable: false,
        displayPrice: false,
        contactMode: strategy.contactMode,
        latitude: currentUserLocation?.latitude,
        longitude: currentUserLocation?.longitude,
        // Info is personal-only: never attached to a business.
        businessId: null,
        productMediaResources: mediaResources,
        ...buildInfoRequestParts(newProduct.infoDraft),
      },
    };
  }

  return {
    product: {
      title: description.title,
      description: description.description,
      sellingPrice: strategy.askPrice,
      suggestedPrice: strategy.suggestion?.suggestedPrice,
      category: description.category,
      sellerId: genericUtility.getCurrentUserId(),
      details: mergedDetails,
      listingType: essentials.listingType,
      condition: keyAttributes?.mandatoryAttributes?.["Condition"] || undefined,
      isNegotiable: strategy.isNegotiable ?? false,
      latitude: currentUserLocation?.latitude,
      longitude: currentUserLocation?.longitude,
      contactMode: strategy.contactMode,
      displayPrice: strategy.displayPrice == true,
      businessId: essentials.businessId ?? null,
      // Wire field is `productMediaResources` (backend contract) — the local
      // variable keeps its short name, only the outgoing key changes.
      productMediaResources: mediaResources,
      ...variantsPayload,
    },
  };
}

/**
 * Wire shape for bulkCreateProduct. Both flags exist so the request can be
 * reshaped in one line when the backend is confirmed, without touching the
 * paste format, the preview or the modal.
 *
 * ⚠️ UNVERIFIED against a spec. bulkCreateProduct is not in the OpenAPI doc this
 * repo has been reconciled against; confirm before relying on it in production.
 *
 * Both are set to send EXACTLY what the curator pasted — a bare array of
 * `{ product: {...} }` items (Sai, 2026-08-08, with a sample payload). An
 * earlier pass unwrapped each item and wrapped the array in `{ products: [...] }`;
 * that was wrong on both counts and is what this pair of constants now pins
 * down.
 */
/** true → each entry stays `{ product: {...} }`. false → the bare inner object. */
const BULK_ITEMS_ARE_WRAPPED: boolean = true;
/** true → body IS the array. false → body is `{ products: [...] }`. */
const BULK_SENDS_BARE_ARRAY: boolean = true;

/**
 * What goes on the wire. A union rather than `any` because it's fully knowable
 * from the two flags above — the RESPONSE stays `any` (see
 * ProductApiService.bulkCreateProducts) since its shape genuinely isn't
 * confirmed, and inventing an interface for it would look authoritative while
 * being a guess.
 */
export type BulkCreateProductsBody = unknown[] | { products: unknown[] };

/** The bits of the target business a pasted payload can't know about itself. */
export interface BulkProductContext {
  businessId: string;
  latitude?: number | null;
  longitude?: number | null;
}

/**
 * Body for `POST /api/v1/products/bulkCreateProduct`.
 *
 * The pasted list is N create-product payloads — the same `{ product: {...} }`
 * shape buildCreateProductPayload emits, so a curator can build one product in
 * the normal flow, copy the request body, and multiply it. That symmetry is the
 * whole reason the paste format wasn't invented fresh, and it's why the default
 * flags above send the array through untouched rather than reshaping it.
 *
 * Three fields are overwritten rather than trusted from the paste:
 *   - `businessId`, so a payload copied from one shop can't silently seed
 *     another. The curator picked the business by opening the card; that beats
 *     a field buried 40 lines into a blob.
 *   - `latitude` / `longitude`, from the setup row — a pasted coordinate is
 *     almost always a leftover from wherever the payload was authored, and
 *     products are searched by distance.
 *
 * The spread order matters and is deliberate: `{ ...inner, businessId }` leaves
 * an EXISTING businessId in its original position with a new value, so a
 * round-tripped payload is byte-comparable to what was pasted. Building the
 * object the other way round would silently reorder every item's keys.
 *
 * Coordinates are only stamped when the setup actually has them; a null is
 * omitted entirely rather than sent, so the backend keeps whatever default it
 * would apply instead of receiving an explicit "nowhere".
 *
 * Tolerant of both wrapped and flat entries on the way IN (a curator pasting
 * inner objects shouldn't get a cryptic failure) while emitting exactly one
 * shape on the way OUT.
 */
function buildBulkCreateProductsPayload(
  items: unknown[],
  context: BulkProductContext
): BulkCreateProductsBody {
  const products = items.map((item) => {
    const isWrapped =
      !!item && typeof item === "object" && "product" in (item as any);
    const inner = isWrapped ? ((item as any).product ?? {}) : (item ?? {});

    const withContext: Record<string, any> = {
      ...(inner as Record<string, any>),
      businessId: context.businessId,
    };

    if (context.latitude != null && context.longitude != null) {
      withContext.latitude = context.latitude;
      withContext.longitude = context.longitude;
    }

    // Re-wrapping from the ORIGINAL item preserves any sibling keys a future
    // payload might carry alongside `product`, instead of replacing the entry
    // with a freshly minted one-key object.
    if (!BULK_ITEMS_ARE_WRAPPED) return withContext;
    return isWrapped
      ? { ...(item as any), product: withContext }
      : { product: withContext };
  });

  return BULK_SENDS_BARE_ARRAY ? products : { products };
}

/**
 * Turns already-existing products (the resolved members of a listing
 * collection) into bulk-upload payload entries — the same `{ product: {...} }`
 * shape buildCreateProductPayload emits for one product, reusing that SAME
 * mapping (via convertProductDetailsToNewProduct, including cloned media) so
 * this can't silently drift from what a curator building one product by hand
 * would get.
 *
 * `sellerId` is forced to null: it would otherwise be the curator's own id
 * (buildCreateProductPayload's default), and cloning a listing shouldn't
 * quietly attribute it to whoever ran the clone. `businessId` / `latitude` /
 * `longitude` DO come across from the source; the curator overwrites them with
 * the Business ID and Location buttons.
 *
 * ⚠️ The `details` map is re-applied from the source AFTER the round trip.
 * convertProductDetailsToNewProduct exists for the EDIT modal, where its
 * `ignoredKeys` list deliberately hides keys from the optional-attribute
 * editor — but that filter also means a straight round trip silently drops
 * part of the map. A clone has to carry the listing's attributes whole, so the
 * source map is merged back over the derived one by mergeClonedDetails.
 */
/**
 * Combines a cloned listing's own `details` with the ones buildCreateProductPayload
 * derived, under two rules:
 *
 *  1. Server-owned keys (SERVER_OWNED_DETAIL_KEYS — `pid`, `created_at`,
 *     `sellerid`, `offercounts`…) are dropped. They describe the SOURCE row,
 *     and a new product inheriting them is how a clone ends up claiming to
 *     have been created before it existed.
 *  2. Matching is case-INSENSITIVE, and the result is guaranteed to hold no
 *     two keys that differ only by case. `details` is a free-form map, so
 *     `condition` alongside `Condition` — whether that comes from source-vs-
 *     derived or from a source that already carried both — leaves the backend
 *     picking one at random. This is the same failure handleApplySection
 *     case-folds to avoid.
 *
 *     Derived wins over source, being the normalised spelling just computed
 *     from the source's own value. If the SOURCE itself carried two spellings
 *     of one key, exactly one survives — which one is arbitrary (both reach
 *     the derived map as separate optional attributes, and the last processed
 *     takes the slot). The guarantee here is only that one survives; a source
 *     shipping `section` and `Section` with different values is malformed
 *     data, and this collapses it rather than forwarding the ambiguity.
 *
 *  3. `sectionId` is dropped from BOTH sides, same as SERVER_OWNED_DETAIL_KEYS.
 *     It's business-scoped (validated against the product's businessId server-
 *     side) — a clone's businessId is routinely reassigned to a different
 *     business by whatever bulk flow consumes this output, and forwarding the
 *     source's sectionId unchanged would either fail the create outright
 *     (VALIDATION_ERROR, wrong business) or, worse, silently land under an
 *     unrelated section that only coincidentally shares that id. Same
 *     rationale as forcing `sellerId = null` below: a clone shouldn't quietly
 *     inherit placement info that only made sense for the source.
 */
function mergeClonedDetails(
  sourceDetails: Record<string, any> | undefined,
  derivedDetails: Record<string, any> | undefined
): Record<string, any> {
  const out: Record<string, any> = {};
  /** lowercased key → the spelling actually written to `out`. */
  const claimed = new Map<string, string>();
  const isDropped = (lower: string) => SERVER_OWNED_DETAIL_KEYS.has(lower) || lower === "sectionid";

  // Source first, so the clone keeps the listing's own attribute order.
  Object.entries(sourceDetails ?? {}).forEach(([key, value]) => {
    const lower = key.toLowerCase();
    if (isDropped(lower)) return;
    if (claimed.has(lower)) return;
    claimed.set(lower, key);
    out[key] = value;
  });

  // Derived second — it wins on both value AND spelling, since it holds the
  // normalised form (`Condition`) that buildCreateProductPayload computed. The
  // source's variant is removed rather than left beside it.
  Object.entries(derivedDetails ?? {}).forEach(([key, value]) => {
    const lower = key.toLowerCase();
    if (isDropped(lower)) return;
    const existing = claimed.get(lower);
    if (existing !== undefined && existing !== key) delete out[existing];
    claimed.set(lower, key);
    out[key] = value;
  });

  return out;
}

function buildBulkPayloadFromExistingProducts(
  products: ProductDetailsModal[]
): unknown[] {
  return products.map((product) => {
    const modalState = convertProductDetailsToNewProduct(product);
    const built = buildCreateProductPayload(modalState, null);

    built.product.sellerId = null;
    built.product.businessId = product.businessId ?? null;
    built.product.latitude = product.latitude;
    built.product.longitude = product.longitude;

    // Every attribute the source carried, plus whatever the round trip
    // derived — minus the source row's own server-owned metadata.
    built.product.details = mergeClonedDetails(
      product.details,
      built.product.details
    );

    // A clone is a brand-new product — its variants (if any) must become
    // brand-new rows too. Stripping variantId here (rather than teaching
    // buildCreateProductPayload to do it) keeps that function's generic
    // create/update behavior untouched: a real update legitimately wants to
    // keep sending variantId so the backend's merge-by-id matches existing
    // rows instead of duplicating them.
    if (Array.isArray((built.product as any).variants)) {
      (built.product as any).variants = (built.product as any).variants.map(
        (v: Record<string, unknown>) => {
          const { variantId, ...rest } = v;
          return rest;
        }
      );
    }

    // Plain product fields with no slot in CreateProductModalState, so the
    // round trip can't carry them — copied straight across.
    if (product.location !== undefined) built.product.location = product.location;
    if (product.phone !== undefined) built.product.phone = product.phone;
    if (product.sellingPoints !== undefined) {
      built.product.sellingPoints = product.sellingPoints;
    }

    return built;
  });
}

function buildSearchPayload(
  searchString: string,
  userPreferences: UserPreferences
) {
  const userLocation = genericUtility.resolveUserLocation(userPreferences);
  return {
    searchString,
    latitude: userLocation?.userLatitude,
    longitude: userLocation?.userLongitude,
    radius: 5,
    sortBy: userPreferences.searchSortBy,
    hyperLocalEnabled: userPreferences.isHyperLocalEnabled,
    hyperLocalDistance: userPreferences.hyperLocalDistance ? userPreferences.hyperLocalDistance / 1000 : 0.2,
  };
}


function buildCategorySearchPayload(
  category: string,
  userPreferences: UserPreferences
) {
  const userLocation = genericUtility.resolveUserLocation(userPreferences);
  return {
    category,
    latitude: userLocation?.userLatitude,
    longitude: userLocation?.userLongitude,
    radius: 5,
    sortBy: userPreferences.searchSortBy,
    hyperLocalEnabled: userPreferences.isHyperLocalEnabled,
    hyperLocalDistance: userPreferences.hyperLocalDistance ? userPreferences.hyperLocalDistance / 1000 : 0.2,
    count: 5,
  };
}

function buildLocalFindsSearchPayload(
  searchString: string,
  userPreferences: UserPreferences
) {
  const userLocation = genericUtility.resolveUserLocation(userPreferences);
  return {
    searchString,
    latitude: userLocation?.userLatitude,
    longitude: userLocation?.userLongitude,
    radius: userPreferences.ninefindsRadius,
    sortBy: userPreferences.searchSortBy,
  };
}

// Single exported service object
const ProductConvertorService = {
  buildCreateProductPayload,
  buildBulkCreateProductsPayload,
  buildBulkPayloadFromExistingProducts,
  buildSearchPayload,
  buildCategorySearchPayload,
  buildLocalFindsSearchPayload,
};

export default ProductConvertorService;
// ...existing code...
